/** User-scoped backup and export (JSON · CSV · PDF) — Step 9. */
import { jsPDF } from "jspdf";
import { captureWorkspaceSnapshot, listRestorePoints, RESTORE_SCOPES } from "../accounts/restorePoints.js";
import { accountBundle } from "../accounts/store.js";
import { getLifecycle } from "../api/accountLifecycle.js";
import { listClientDocuments } from "../db/documents.js";
import { writeAudit } from "../db/engine.js";
import { preferenceProgress, prettyValue } from "../utils/formatters.js";
import { sellerProgress } from "../utils/sellerSteps.js";
import { EXPORT_FOOTER_DISCLAIMER, PLATFORM_DISCLAIMER } from "../legal/copy.js";
import { BACKUP_VERSION, detectBackupPlatform, platformLabel, saveBackupFile } from "./platform.js";

function readJson(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function csvEscape(value) {
  return `"${String(value ?? "").replace(/"/g, '""')}"`;
}

function flattenObject(obj, prefix = "", rows = []) {
  if (obj == null) return rows;
  if (typeof obj !== "object" || Array.isArray(obj)) {
    rows.push([prefix || "value", prettyValue(obj)]);
    return rows;
  }
  Object.entries(obj).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) flattenObject(value, path, rows);
    else rows.push([path, prettyValue(value)]);
  });
  return rows;
}

function scopesForRole(role) {
  switch (role) {
    case "buyer":
      return [RESTORE_SCOPES.buyer, RESTORE_SCOPES.messaging];
    case "seller":
      return [RESTORE_SCOPES.seller, RESTORE_SCOPES.messaging];
    case "broker":
      return [RESTORE_SCOPES.broker_desk, RESTORE_SCOPES.settings, RESTORE_SCOPES.messaging, RESTORE_SCOPES.account];
    case "agent":
      return [RESTORE_SCOPES.agent_desk, RESTORE_SCOPES.settings, RESTORE_SCOPES.messaging, RESTORE_SCOPES.account];
    case "admin":
    case "corporate":
      return [
        RESTORE_SCOPES.agent_desk,
        RESTORE_SCOPES.settings,
        RESTORE_SCOPES.messaging,
        RESTORE_SCOPES.account,
      ];
    default:
      return [RESTORE_SCOPES.settings];
  }
}

export function collectUserBackup(session) {
  if (!session?.userId) throw new Error("Sign in to export your data.");
  const userId = session.userId;
  const role = session.role || "unknown";
  const platform = detectBackupPlatform();
  const scopes = scopesForRole(role);
  const data = {};
  scopes.forEach((scope) => {
    const snapshot = captureWorkspaceSnapshot(userId, scope);
    if (snapshot) data[scope] = snapshot;
  });
  const restorePoints = listRestorePoints(userId, { limit: 120 });
  const documents = listClientDocuments(userId, role === "seller" ? "seller" : role === "buyer" ? "buyer" : "");
  const bundle = accountBundle(userId);
  const lifecycle = getLifecycle(userId);

  let progress = null;
  if (role === "buyer" && data.buyer?.buyer?.preferences) {
    progress = preferenceProgress(data.buyer.buyer.preferences);
  } else if (role === "seller" && data.seller?.seller?.portfolio) {
    progress = sellerProgress(data.seller.seller.portfolio);
  }

  return {
    version: BACKUP_VERSION,
    exportedAt: new Date().toISOString(),
    platform,
    platformLabel: platformLabel(platform),
    product: "rechecklist",
    session: {
      userId,
      role,
      name: session.name || "",
      email: session.email || "",
    },
    summary: {
      restorePointCount: restorePoints.length,
      documentCount: documents.length,
      progress,
      lifecycleStatus: lifecycle?.status || "active",
      accountStatus: bundle?.account?.status || "",
      planId: bundle?.subscription?.plan_id || "",
    },
    data,
    documents: documents.map((row) => ({
      id: row.id,
      file_name: row.file_name,
      kind: row.kind,
      mime_type: row.mime_type,
      size: row.size,
      created_at: row.created_at,
    })),
    restorePoints: restorePoints.map((row) => ({
      id: row.id,
      at: row.at,
      label: row.label,
      scope: row.scope,
      source: row.source,
    })),
  };
}

function backupStem(session) {
  const role = session?.role || "user";
  const stamp = new Date().toISOString().slice(0, 10);
  return `rechecklist-${role}-${stamp}`;
}

function recordExportAudit(session, format, meta = {}) {
  if (!session?.userId) return;
  try {
    writeAudit({
      user_id: session.userId,
      action_type: "backup.export",
      transaction_id: null,
      metadata: { format, ...meta },
    });
  } catch {
    /* optional */
  }
}

export function buildBackupCsv(backup) {
  const lines = [
    ["section", "field", "value"].map(csvEscape).join(","),
    ["meta", "exportedAt", backup.exportedAt].map(csvEscape).join(","),
    ["meta", "platform", backup.platformLabel].map(csvEscape).join(","),
    ["meta", "role", backup.session.role].map(csvEscape).join(","),
    ["meta", "restorePoints", backup.summary.restorePointCount].map(csvEscape).join(","),
    ["meta", "documents", backup.summary.documentCount].map(csvEscape).join(","),
  ];
  if (backup.summary.progress) {
    lines.push(
      ["progress", "percent", backup.summary.progress.percent].map(csvEscape).join(","),
      ["progress", "sectionsDone", backup.summary.progress.done].map(csvEscape).join(","),
      ["progress", "sectionsTotal", backup.summary.progress.total].map(csvEscape).join(",")
    );
  }
  Object.entries(backup.data || {}).forEach(([scope, payload]) => {
    flattenObject(payload, scope).forEach(([field, value]) => {
      if (!value) return;
      lines.push([scope, field, value].map(csvEscape).join(","));
    });
  });
  backup.documents.forEach((row) => {
    lines.push(["document", row.file_name, `${row.kind} · ${row.size} bytes`].map(csvEscape).join(","));
  });
  return lines.join("\n");
}

export function buildBackupPdf(backup) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const margin = 18;
  let y = 20;
  const write = (text, { bold = false, size = 10, gap = 6 } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const wrapped = doc.splitTextToSize(String(text), 180);
    wrapped.forEach((line) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      doc.text(line, margin, y);
      y += gap * 0.85;
    });
  };

  doc.setTextColor(14, 61, 44);
  write("REChecklist Data Export", { bold: true, size: 18, gap: 10 });
  doc.setTextColor(44, 44, 44);
  write(`Prepared ${new Date(backup.exportedAt).toLocaleString()} · ${backup.platformLabel}`);
  write(`Account: ${backup.session.name || backup.session.email || backup.session.userId} · ${backup.session.role}`);
  y += 2;
  write("Summary", { bold: true, size: 13, gap: 8 });
  write(`Restore points on device: ${backup.summary.restorePointCount}`);
  write(`Documents indexed: ${backup.summary.documentCount}`);
  if (backup.summary.progress) {
    write(`Checklist progress: ${backup.summary.progress.percent}% (${backup.summary.progress.done}/${backup.summary.progress.total})`);
  }
  if (backup.summary.lifecycleStatus) write(`Lifecycle status: ${backup.summary.lifecycleStatus}`);
  y += 2;
  write("Included scopes", { bold: true, size: 13, gap: 8 });
  Object.keys(backup.data || {}).forEach((scope) => write(`· ${scope}`));
  y += 2;
  write("Your responsibility", { bold: true, size: 13, gap: 8 });
  write(
    "Store this export on a Windows PC or Apple Mac folder you control. JSON is the complete archive; CSV is for review; PDF is a readable summary. Restore points are device-local and may be purged — exports are your long-term copy."
  );
  y += 2;
  doc.setTextColor(92, 74, 68);
  write(PLATFORM_DISCLAIMER, { size: 9, gap: 5 });
  write(EXPORT_FOOTER_DISCLAIMER, { size: 9, gap: 5 });
  return doc;
}

export async function exportUserBackupJson(session, { usePicker = true } = {}) {
  const backup = collectUserBackup(session);
  const filename = `${backupStem(session)}.json`;
  const body = JSON.stringify(backup, null, 2);
  const result = usePicker
    ? await saveBackupFile({
        filename,
        mime: "application/json",
        body,
        description: "REChecklist JSON backup",
      })
    : { method: "download", filename };
  if (!usePicker) {
    const blob = new Blob([body], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
  recordExportAudit(session, "json", { filename: result.filename, method: result.method });
  return backup;
}

export async function exportUserBackupCsv(session, { usePicker = true } = {}) {
  const backup = collectUserBackup(session);
  const filename = `${backupStem(session)}.csv`;
  const body = buildBackupCsv(backup);
  const result = usePicker
    ? await saveBackupFile({
        filename,
        mime: "text/csv",
        body,
        description: "REChecklist CSV export",
      })
    : { method: "download", filename };
  if (!usePicker) {
    const blob = new Blob([body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
  recordExportAudit(session, "csv", { filename: result.filename, method: result.method });
  return backup;
}

export async function exportUserBackupPdf(session, { usePicker = false } = {}) {
  const backup = collectUserBackup(session);
  const doc = buildBackupPdf(backup);
  const filename = `${backupStem(session)}.pdf`;
  if (usePicker && typeof window !== "undefined" && window.showSaveFilePicker) {
    const blob = doc.output("blob");
    const result = await saveBackupFile({
      filename,
      mime: "application/pdf",
      body: blob,
      description: "REChecklist PDF summary",
    });
    recordExportAudit(session, "pdf", { filename: result.filename, method: result.method });
    return backup;
  }
  doc.save(filename);
  recordExportAudit(session, "pdf", { filename, method: "download" });
  return backup;
}
